/**
 * useSSE — Server-Sent Events hook for real-time project updates.
 *
 * Connects to the backend SSE stream for a project and automatically
 * invalidates relevant React Query caches when events arrive.
 * Also handles presence updates and activity toasts.
 */

import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/api/hooks";
import { usePresenceStore } from "@/stores/presence";
import { useAuthStore } from "@/stores/auth";
import { useAutonomousRunStore } from "@/stores/autonomousRun";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api/v1";

/** Event types emitted by the backend SSE stream.
 * Backend uses dot-notation (entity.created) which the SSE endpoint
 * normalizes to underscore (entity_created) for EventSource compat. */
type SSEEventType =
  | "qa_answer"
  | "qa_edit"
  | "qa_session_failed"
  | "qa_processing_complete"
  | "workflow_started"
  | "workflow_completed"
  | "workflow_failed"
  | "workflow_cancelled"
  | "workflow_awaiting_input"
  | "workflow_task_started"
  | "workflow_task_completed"
  | "workflow_task_failed"
  | "entity_created"
  | "entity_updated"
  | "entity_deleted"
  | "entity_status_changed"
  | "conflicts_detected"
  | "notification_alert"
  | "notification"
  | "presence_update"
  | "autonomous_goal_started"
  | "autonomous_wave_started"
  | "autonomous_entity_created"
  | "autonomous_wave_completed"
  | "autonomous_goal_completed"
  | "autonomous_goal_failed"
  | "autonomous_goal_cancelled"
  | "message";

interface SSEEvent {
  event_type: SSEEventType;
  session_id?: string;
  entity_type?: string;
  entity_id?: string;
  project_id?: string;
  active_users?: Array<{ user_id: string; display_name: string; page: string }>;
  event_user_id?: string;
  [key: string]: unknown;
}

/** Map backend entity_type to React Query key prefix. */
function entityTypeToQueryKey(entityType: string): readonly string[] | null {
  const map: Record<string, readonly string[]> = {
    objective: queryKeys.objectives.all,
    component: queryKeys.components.all,
    test_case: queryKeys.testCases.all,
    user_story: queryKeys.userStories.all,
    impl_task: queryKeys.tasks.all,
    defect: queryKeys.defects.all,
    document: queryKeys.documents.all,
    sprint: queryKeys.sprints.all,
  };
  return map[entityType] ?? null;
}

/** Format entity type for display. */
function formatEntityType(type: string): string {
  const map: Record<string, string> = {
    objective: "objective",
    component: "component",
    test_case: "test case",
    user_story: "user story",
    impl_task: "task",
    defect: "defect",
    document: "document",
    sprint: "sprint",
    project: "project",
  };
  return map[type] ?? type.replace(/_/g, " ");
}

/** Format action for display. */
function formatAction(action: string): string {
  const map: Record<string, string> = {
    entity_created: "created",
    entity_updated: "updated",
    entity_deleted: "deleted",
    entity_status_changed: "changed status of",
  };
  return map[action] ?? action;
}

/**
 * Unwrap an SSE frame's real payload.
 *
 * sse_service.broadcast() builds an envelope `{id, event, data}` where `data`
 * is the payload ALREADY json-encoded, and the endpoint then json-encodes the
 * whole envelope. The hook parses once, so `frame.entity_type` is undefined
 * and the payload actually lives at `frame.data` as a string. Reading fields
 * off the frame directly silently yields undefined — which is how a handler
 * can look correct, run, and do nothing.
 */
export function ssePayload(frame: Record<string, unknown>): Record<string, unknown> {
  const inner = frame?.data;
  if (typeof inner === "string") {
    try {
      const parsed = JSON.parse(inner);
      if (parsed && typeof parsed === "object") return parsed as Record<string, unknown>;
    } catch {
      /* not JSON — fall through to the frame itself */
    }
  }
  if (inner && typeof inner === "object") return inner as Record<string, unknown>;
  return frame ?? {};
}

export function useSSE(projectId: string | undefined) {
  const qc = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEventIdRef = useRef<string | null>(null);
  const retryCountRef = useRef(0);
  const setActiveUsers = usePresenceStore((s) => s.setActiveUsers);
  const addToast = usePresenceStore((s) => s.addToast);
  const currentUserId = useAuthStore((s) => s.user?.id);

  const handleEvent = useCallback(
    (event: MessageEvent) => {
      try {
        const data: SSEEvent = JSON.parse(event.data);
        const type = data.event_type ?? (event.type as SSEEventType);

        // Store last event ID for resume
        if (event.lastEventId) {
          lastEventIdRef.current = event.lastEventId;
        }

        switch (type) {
          case "qa_answer":
          case "qa_edit":
          case "qa_session_failed":
          case "qa_processing_complete":
            {
            // The payload is double-encoded — it lives at frame.data
            // as a JSON string. Reading it off the frame yields
            // undefined and this handler silently does nothing.
            const p = ssePayload(data as unknown as Record<string, unknown>);
            // Invalidate QA session queries
            if (p.session_id) {
              qc.invalidateQueries({
                queryKey: queryKeys.qaSessions.detail(p.session_id as string),
              });
            }
            qc.invalidateQueries({ queryKey: queryKeys.qaSessions.all });
                        }
            break;

          case "workflow_started":
          case "workflow_completed":
          case "workflow_failed":
          case "workflow_cancelled":
          case "workflow_task_started":
          case "workflow_task_completed":
          case "workflow_task_failed":
          case "workflow_awaiting_input":
            {
            // The payload is double-encoded — it lives at frame.data
            // as a JSON string. Reading it off the frame yields
            // undefined and this handler silently does nothing.
            const p = ssePayload(data as unknown as Record<string, unknown>);
            qc.invalidateQueries({ queryKey: ["workflows", "runs"] });
            if (p.entity_type) {
              const entityQueryKey = entityTypeToQueryKey(p.entity_type as string);
              if (entityQueryKey) {
                qc.invalidateQueries({ queryKey: entityQueryKey });
              }
            }
                        }
            break;

          case "conflicts_detected":
            // Detection stopped being synchronous with the create/update in
            // #2363 (it invokes an LLM), so the banner's one fetch can land
            // BEFORE the conflicts exist. This is how it learns they arrived.
            {
              const payload = ssePayload(data as unknown as Record<string, unknown>);
              // A conflict row names TWO entities. Refreshing only the trigger
              // leaves the COUNTERPART's banner stale — and cross-stream
              // detection deliberately notifies that entity's owner, so they
              // are the likeliest person with the panel open. Refresh every
              // entity the event names, falling back to the trigger pair for
              // any older event that predates the `entities` field.
              const named = Array.isArray(payload.entities)
                ? (payload.entities as Array<{ type?: string; id?: string }>)
                : [];
              const pairs = named.length
                ? named
                : [{ type: payload.entity_type as string, id: payload.entity_id as string }];
              for (const { type, id } of pairs) {
                if (type && id) {
                  qc.invalidateQueries({ queryKey: ["entity-conflicts", type, id] });
                }
              }
            }
            qc.invalidateQueries({ queryKey: ["conflicts"] });
            break;

          case "entity_created":
          case "entity_updated":
          case "entity_deleted":
          case "entity_status_changed":
            {
            // The payload is double-encoded — it lives at frame.data as a
            // JSON string, so reading it off the frame yields undefined and
            // this handler silently did nothing: an entity whose status
            // changed never refreshed the list showing it.
            //
            // MEASURED before unwrapping: this also gates an activity toast,
            // but no entity.* broadcast carries event_user_id (only
            // presence.py sends it), so that branch stays dormant either
            // way. Unwrapping activates the invalidation ONLY.
            //
            // Enforced by tests/test_entity_sse_payloads_stay_minimal.py,
            // which fails if any entity.* broadcast starts carrying
            // event_user_id. The frontend test below CANNOT catch that — it
            // emits its own fixture — and an earlier version of this comment
            // wrongly claimed it could.
            const p = ssePayload(data as unknown as Record<string, unknown>);
            if (p.entity_type) {
              const entityQueryKey = entityTypeToQueryKey(p.entity_type as string);
              if (entityQueryKey) {
                qc.invalidateQueries({ queryKey: entityQueryKey });
              }
            }
            // Show activity toast for changes by other users
            if (p.event_user_id && p.event_user_id !== currentUserId && p.entity_type) {
              const activeUsers = usePresenceStore.getState().activeUsers;
              const actor = activeUsers.find((u) => u.user_id === p.event_user_id);
              const actorName = actor?.display_name ?? "Someone";
              const entityType = formatEntityType(p.entity_type as string);
              const action = formatAction(type);
              addToast({
                id: `${type}-${p.entity_id}-${Date.now()}`,
                message: `${actorName} ${action} a ${entityType}`,
                type: type === "entity_deleted" ? "warning" : "info",
              });
            }
                        }
            break;

          case "presence_update":
            {
            // The payload is double-encoded — it lives at frame.data as a
            // JSON string, so data.active_users was always undefined and
            // presence never updated from SSE at all. It only appeared to
            // work because the heartbeat POST response also returns
            // active_users, which masked this completely.
            //
            // Safe to activate: the store write takes the SAME value the
            // heartbeat path already feeds it (app/api/v1/presence.py:44-51
            // broadcasts the same `active_users` it returns), so the shape
            // is already proven in production. No toast, no other effect.
            const p = ssePayload(data as unknown as Record<string, unknown>);
            if (Array.isArray(p.active_users)) {
              setActiveUsers(p.active_users as Parameters<typeof setActiveUsers>[0]);
            }
                        }
            break;

          case "notification":
          case "notification_alert":
            qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
            break;

          // ── WAVE 10C: AUTONOMOUS RUN LIFECYCLE ─────────────
          // Hydrate the autonomous-run store so the floating
          // activity indicator + Follow Mode auto-nav can react
          // in real time. Cross-tab: any browser with this project
          // open sees the live run, not just the initiating tab.
          //
          // All seven read their fields off the SSE ENVELOPE, and they
          // are FIXED TOGETHER ON PURPOSE. Every reducer below the first
          // is guarded by `activeRun.goalRunId !== goalRunId`, and today
          // both sides of that comparison are the same empty string — so
          // the run currently renders as wave "0" with 0 tools, and every
          // later wave is dropped by the idempotency check. Unwrapping
          // ONE handler would make a real id face a stored "", the guard
          // would start failing, and waves would stop appearing at all.
          // A partial fix here is worse than the defect.
          case "autonomous_goal_started":
            {
            const p = ssePayload(data as unknown as Record<string, unknown>);
            useAutonomousRunStore.getState().startRun({
              goalRunId: String(p.goal_run_id ?? ""),
              startedAt: p.started_at as string | undefined,
              messagePreview: p.message_preview as string | undefined,
            });
                        }
            break;

          case "autonomous_wave_started":
            {
            const p = ssePayload(data as unknown as Record<string, unknown>);
            useAutonomousRunStore.getState().markWaveStarted({
              goalRunId: String(p.goal_run_id ?? ""),
              waveNumber: Number(p.wave_number ?? 0),
              toolCount: Number(p.tool_count ?? 0),
            });
                        }
            break;

          case "autonomous_entity_created":
            {
            const p = ssePayload(data as unknown as Record<string, unknown>);
            if (p.entity_id && p.entity_type && p.goal_run_id) {
              useAutonomousRunStore.getState().appendEntity({
                goalRunId: String(p.goal_run_id),
                waveNumber: Number(p.wave_number ?? 0),
                positionInWave: Number(p.position_in_wave ?? 0),
                waveTotal: Number(p.wave_total ?? 0),
                entityType: String(p.entity_type),
                entityId: String(p.entity_id),
                title: String(p.title ?? ""),
                tool: String(p.tool ?? ""),
                action:
                  (p.action as "create" | "update" | undefined) ?? "create",
                createdAt: Date.now(),
              });
              // Refresh the list cache so the row actually appears
              const entityQueryKey = entityTypeToQueryKey(
                p.entity_type as string
              );
              if (entityQueryKey) {
                qc.invalidateQueries({ queryKey: entityQueryKey });
              }
            }
                        }
            break;

          case "autonomous_wave_completed":
            {
            const p = ssePayload(data as unknown as Record<string, unknown>);
            useAutonomousRunStore.getState().markWaveCompleted({
              goalRunId: String(p.goal_run_id ?? ""),
              waveNumber: Number(p.wave_number ?? 0),
              elapsedMs: Number(p.elapsed_ms ?? 0),
              succeeded:
                p.succeeded !== undefined
                  ? Number(p.succeeded)
                  : undefined,
            });
                        }
            break;

          case "autonomous_goal_completed":
            {
            const p = ssePayload(data as unknown as Record<string, unknown>);
            useAutonomousRunStore.getState().completeRun({
              goalRunId: String(p.goal_run_id ?? ""),
              totalWaves: Number(p.total_waves ?? 0),
              totalEntitiesCreated: Number(p.total_entities_created ?? 0),
              totalActions: Number(p.total_actions ?? 0),
              elapsedMs: Number(p.elapsed_ms ?? 0),
              stopReason: (p.stop_reason as string | null) ?? null,
            });
                        }
            break;

          case "autonomous_goal_failed":
            {
            const p = ssePayload(data as unknown as Record<string, unknown>);
            useAutonomousRunStore.getState().failRun({
              goalRunId: String(p.goal_run_id ?? ""),
              totalWaves: Number(p.total_waves ?? 0),
              totalActions: Number(p.total_actions ?? 0),
              elapsedMs: Number(p.elapsed_ms ?? 0),
              stopReason: (p.stop_reason as string | null) ?? null,
            });
                        }
            break;

          case "autonomous_goal_cancelled":
            {
            const p = ssePayload(data as unknown as Record<string, unknown>);
            useAutonomousRunStore.getState().cancelRun({
              goalRunId: String(p.goal_run_id ?? ""),
              totalWaves: Number(p.total_waves ?? 0),
              totalEntitiesCreated: Number(p.total_entities_created ?? 0),
              totalActions: Number(p.total_actions ?? 0),
              elapsedMs: Number(p.elapsed_ms ?? 0),
              stopReason: (p.stop_reason as string | null) ?? null,
            });
                        }
            break;
        }
      } catch {
        // Ignore malformed events
      }
    },
    [qc, setActiveUsers, addToast, currentUserId]
  );

  const connect = useCallback(() => {
    if (!projectId) return;

    const token = localStorage.getItem("ftl_access_token");
    // SSE doesn't support custom headers, so pass token as query param
    const url = `${API_BASE}/sse/stream?project_id=${projectId}${token ? `&token=${token}` : ""}`;

    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onopen = () => {
      retryCountRef.current = 0; // Reset backoff on successful connection
    };

    // Listen for specific event types (underscore format, normalized by SSE endpoint)
    const eventTypes: SSEEventType[] = [
      "qa_answer",
      "qa_edit",
      "qa_session_failed",
      "qa_processing_complete",
      "workflow_started",
      "workflow_completed",
      "workflow_failed",
      "workflow_cancelled",
      "workflow_awaiting_input",
      "workflow_task_started",
      "workflow_task_completed",
      "workflow_task_failed",
      "entity_created",
      "entity_updated",
      "entity_deleted",
      "entity_status_changed",
      "conflicts_detected",
      "notification",
      "notification_alert",
      "presence_update",
      "autonomous_goal_started",
      "autonomous_wave_started",
      "autonomous_entity_created",
      "autonomous_wave_completed",
      "autonomous_goal_completed",
      "autonomous_goal_failed",
      "autonomous_goal_cancelled",
      "message",
    ];

    for (const type of eventTypes) {
      es.addEventListener(type, handleEvent);
    }

    // Also handle generic message events
    es.onmessage = handleEvent;

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;

      // Reconnect with exponential backoff (max 30s)
      retryCountRef.current = (retryCountRef.current ?? 0) + 1;
      const delay = Math.min(30000, 1000 * Math.pow(2, retryCountRef.current - 1));
      reconnectTimeoutRef.current = setTimeout(connect, delay);
    };
  }, [projectId, handleEvent]);

  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [connect]);
}
